import {
  Badge,
  Box,
  Button,
  ChakraSelect,
  Flex,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@elkaso-app/web-design';
import {
  priceImportRowsEndpointIdentifier,
  PriceImportRun,
  priceImportsEndpointIdentifier,
  useUpdatePriceImportMappingApi,
} from 'apis/price-imports';
import {
  ASSIGNABLE_TARGETS,
  buildDraftFromRun,
  buildSpecFromDraft,
  MappingDraft,
  validateDraft,
} from 'pages/price-imports/utils/mapping-draft';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';

export interface MappingEditorProps {
  run: PriceImportRun;
  readOnly: boolean;
}

interface TargetOption {
  value: string;
  label: string;
}

const IGNORED: TargetOption = { value: '', label: '— ignored —' };
const TARGET_OPTIONS: TargetOption[] = [
  IGNORED,
  ...ASSIGNABLE_TARGETS.map((target) => ({ value: target, label: target })),
];

/**
 * Review and correct the column mapping — inference's output, a saved
 * profile's, or a blank slate for a layout nothing recognised.
 *
 * One row per SOURCE column: the file is the fixed axis, the template field is
 * the assignment. Saving re-adapts the run server-side, which REBUILDS every
 * row — any review decisions already made are discarded, so the save confirms
 * first and says exactly that.
 */
export const MappingEditor = ({ run, readOnly }: MappingEditorProps) => {
  const queryClient = useQueryClient();
  const initial = useMemo(() => buildDraftFromRun(run), [run.id, run.mappingSpec]);
  const [draft, setDraft] = useState<MappingDraft | null>(initial);

  useEffect(() => setDraft(initial), [initial]);

  const { mutateAsync: saveMapping, isLoading } = useUpdatePriceImportMappingApi({
    onSuccess: () => {
      queryClient.invalidateQueries([priceImportsEndpointIdentifier, run.id]);
      queryClient.invalidateQueries([priceImportRowsEndpointIdentifier, run.id]);
    },
  });

  if (!draft) return null;

  const validation = validateDraft(draft);
  const isDirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const priceUnitColumnAssigned = draft.columns.some((column) => column.target === 'price_unit');

  const setTarget = (sourceIndex: number, target: string) =>
    setDraft((current) =>
      current
        ? {
            ...current,
            columns: current.columns.map((column) =>
              column.sourceIndex === sourceIndex ? { ...column, target: (target || null) as never } : column
            ),
          }
        : current
    );

  const handleSave = async () => {
    if (!validation.ok) return;

    // Re-adapting rebuilds every row: accepted matches, prices typed in review,
    // holds — all of it. Say so before doing it.
    const hasDecisions = run.totalRows > 0;
    // eslint-disable-next-line no-alert
    if (
      hasDecisions &&
      !window.confirm(
        `Save this mapping and re-read the file? The ${run.totalRows} rows and any decisions on them are rebuilt from scratch.`
      )
    ) {
      return;
    }

    await saveMapping({ runId: run.id, spec: buildSpecFromDraft(draft) }).catch(() => undefined);
  };

  return (
    <Box maxW='860px' mt='16px'>
      <Flex align='center' justify='space-between' mb='8px' gap='12px' wrap='wrap'>
        <Text fontSize='sm' fontWeight='semibold'>
          Column mapping
        </Text>

        {run.mappingSource === 'llm' && run.mappingSpec && (
          <Badge colorScheme='purple' variant='subtle'>
            Read automatically · {Math.round((run.mappingSpec.confidence ?? 0) * 100)}% confident
          </Badge>
        )}
      </Flex>

      {run.mappingSource === 'llm' && run.mappingSpec?.notes && (
        <Text fontSize='xs' color='gray.600' mb='8px'>
          Reader’s note: {run.mappingSpec.notes}
        </Text>
      )}

      <Box overflowX='auto' border='1px solid' borderColor='gray.200' borderRadius='8px'>
        <Table size='sm' variant='simple'>
          <Thead bg='gray.50'>
            <Tr>
              <Th width='26%'>Column in the file</Th>
              <Th width='40%'>Sample values</Th>
              <Th width='34%'>Reads as</Th>
            </Tr>
          </Thead>
          <Tbody>
            {draft.columns.map((column) => (
              <Tr key={column.sourceIndex}>
                <Td>
                  <Text fontSize='sm' fontWeight='medium'>
                    {column.header}
                  </Text>
                  {column.original && column.original.transform.kind !== 'identity' && (
                    <Badge variant='subtle' textTransform='none' fontWeight='medium' mt='2px'>
                      {column.original.transform.kind}
                    </Badge>
                  )}
                </Td>
                <Td>
                  <Text fontSize='xs' color='gray.600' noOfLines={2}>
                    {column.samples.length > 0 ? column.samples.join(' · ') : '—'}
                  </Text>
                </Td>
                <Td>
                  <ChakraSelect
                    size='sm'
                    isDisabled={readOnly}
                    options={TARGET_OPTIONS}
                    value={TARGET_OPTIONS.find((option) => option.value === (column.target ?? '')) ?? IGNORED}
                    menuPortalTarget={document.body}
                    menuPosition='fixed'
                    styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                    onChange={(option: any) => setTarget(column.sourceIndex, option?.value ?? '')}
                  />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      {!priceUnitColumnAssigned && (
        <Flex align='center' gap='8px' mt='10px'>
          <Text fontSize='sm' whiteSpace='nowrap'>
            Every price is per
          </Text>
          <Input
            size='sm'
            maxW='140px'
            placeholder='kg, carton…'
            isDisabled={readOnly}
            value={draft.priceUnitConstant}
            onChange={(event) =>
              setDraft((current) => (current ? { ...current, priceUnitConstant: event.target.value } : current))
            }
          />
          <Text fontSize='xs' color='gray.500'>
            — when the file has no unit column.
          </Text>
        </Flex>
      )}

      {validation.errors.length > 0 && (
        <Box mt='10px' bg='orange.50' borderRadius='6px' p='10px'>
          {validation.errors.map((error) => (
            <Text key={error} fontSize='xs' color='orange.800'>
              {error}
            </Text>
          ))}
        </Box>
      )}

      {!readOnly && (
        <Flex mt='12px' gap='8px'>
          <Button
            size='sm'
            colorScheme='red'
            isLoading={isLoading}
            isDisabled={!validation.ok || !isDirty}
            onClick={handleSave}>
            Save mapping & re-read
          </Button>
          {isDirty && (
            <Button size='sm' variant='ghost' onClick={() => setDraft(initial)}>
              Discard changes
            </Button>
          )}
        </Flex>
      )}
    </Box>
  );
};
